import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  ExternalLink,
  Eye,
  Copy,
  History,
  FileDown,
  FileCode2,
  FolderInput,
  Settings,
  Trash2,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconBtn } from './ui';

export interface OverflowAction {
  id: string;
  label: string;
  icon: ReactNode;
  hint?: string;
  danger?: boolean;
  separatorBefore?: boolean;
  onSelect?: () => void;
}

export interface OverflowMenuProps {
  /** Override the default action set entirely. */
  actions?: OverflowAction[];
  /** Default-action callback. Called for any built-in item without its own onSelect. */
  onAction?: (id: string) => void;
}

const buildDefaultActions = (onAction?: (id: string) => void): OverflowAction[] => [
  {
    id: 'view-published',
    label: 'View published post',
    hint: '↗',
    icon: <Eye className="size-4" />,
    onSelect: () => onAction?.('view-published'),
  },
  {
    id: 'open-wp',
    label: 'Open in WordPress',
    icon: <ExternalLink className="size-4" />,
    onSelect: () => onAction?.('open-wp'),
  },
  {
    id: 'duplicate',
    label: 'Duplicate article',
    icon: <Copy className="size-4" />,
    onSelect: () => onAction?.('duplicate'),
  },
  {
    id: 'history',
    label: 'Article history',
    icon: <History className="size-4" />,
    separatorBefore: true,
    onSelect: () => onAction?.('history'),
  },
  {
    id: 'export-md',
    label: 'Export as Markdown',
    icon: <FileDown className="size-4" />,
    onSelect: () => onAction?.('export-md'),
  },
  {
    id: 'export-html',
    label: 'Export as HTML',
    icon: <FileCode2 className="size-4" />,
    onSelect: () => onAction?.('export-html'),
  },
  {
    id: 'move-folder',
    label: 'Move to folder',
    icon: <FolderInput className="size-4" />,
    onSelect: () => onAction?.('move-folder'),
  },
  {
    id: 'settings',
    label: 'Article settings',
    icon: <Settings className="size-4" />,
    separatorBefore: true,
    onSelect: () => onAction?.('settings'),
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: <Trash2 className="size-4" />,
    danger: true,
    separatorBefore: true,
    onSelect: () => onAction?.('delete'),
  },
];

/**
 * Three-dots overflow menu for DocChrome row 1.
 *
 * Closes on click-outside and Escape. Pass `actions` to fully replace the
 * default 9-item set, or `onAction` to handle the built-in IDs:
 *  open-wp · view-published · duplicate · history · export-md · export-html
 *  · move-folder · settings · delete
 */
export function OverflowMenu({ actions, onAction }: OverflowMenuProps) {
  const [open, setOpen] = useState(false);
  // Peek suppression while the user is on the trigger (the CSS :hover rule
  // can't reach the portal), and the trigger's viewport position for it.
  const [hovered, setHovered] = useState(false);
  const [peekPos, setPeekPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    const update = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const next = { left: r.left + r.width / 2, top: r.top };
      // Only re-render when the trigger actually moved.
      setPeekPos((prev) =>
        prev && prev.left === next.left && prev.top === next.top ? prev : next,
      );
    };
    update();
    window.addEventListener('resize', update);
    // capture-phase so any scrolling ancestor repositions the peek
    window.addEventListener('scroll', update, true);
    // The trigger also moves WITHOUT resize/scroll events: the top bar mounts
    // in a loading state and reflows when article data (score badge, issue
    // chip, title width) arrives — on production latencies the one-shot mount
    // measurement left the bubble anchored where the button USED to be. Poll
    // the rect cheaply and update only on change.
    const poll = window.setInterval(update, 500);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.clearInterval(poll);
    };
  }, []);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const items = actions ?? buildDefaultActions(onAction);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Recurring invitation (same 6s cadence as the AI-Suggest beat): the
          action icons pop up one by one above the button, hold, then all
          vanish at once. Rendered through a body portal with fixed
          positioning: the layout's overflow-x-hidden main would clip anything
          rising above the top bar, and the sticky app header (tenant
          selector) sits at z-10 — the portal escapes both. Decorative only;
          hidden while the menu is open or the trigger is hovered. */}
      {!open &&
        !hovered &&
        peekPos &&
        createPortal(
          <span
            aria-hidden="true"
            data-testid="overflow-peek"
            className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full"
            style={{ left: peekPos.left, top: peekPos.top - 10 }}
          >
            <span className="overflow-peek-bubble relative flex items-center gap-1.5 rounded-lg border border-coral/40 bg-coral-light px-2 py-1.5 shadow-md">
              <span className="overflow-peek-icon flex">
                <Eye className="size-3.5 text-coral-dark" />
              </span>
              <span className="overflow-peek-icon flex">
                <FileDown className="size-3.5 text-coral-dark" />
              </span>
              <span className="overflow-peek-icon flex">
                <Settings className="size-3.5 text-coral-dark" />
              </span>
            </span>
          </span>,
          document.body,
        )}
      <IconBtn
        icon={<MoreHorizontal className="size-4" />}
        label="More actions"
        active={open}
        onClick={() => setOpen((v) => !v)}
        // Visible affordance: bordered card instead of ghost chrome.
        className="border-border bg-card text-foreground shadow-sm"
      />
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-40 w-[230px] rounded-lg border border-border bg-popover p-1 shadow-lg"
        >
          {items.map((it, i) => (
            <div key={it.id}>
              {it.separatorBefore && i > 0 && <div className="my-1 h-px bg-border" />}
              <button
                role="menuitem"
                type="button"
                onClick={() => {
                  setOpen(false);
                  it.onSelect?.();
                }}
                className={cn(
                  'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-left text-[12.5px] font-medium cursor-pointer hover:bg-warm-bg transition-colors',
                  it.danger ? 'text-destructive hover:text-destructive' : 'text-foreground',
                )}
              >
                <span
                  className={cn(
                    'inline-flex shrink-0',
                    it.danger ? 'text-destructive' : 'text-muted-foreground',
                  )}
                >
                  {it.icon}
                </span>
                <span className="flex-1">{it.label}</span>
                {it.hint && (
                  <span className="font-mono text-[10.5px] text-muted-foreground font-medium">
                    {it.hint}
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
